import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Avatar
} from '@mui/material';
import brandService from '../services/brandService';

const BrandSelector = ({ 
  value, 
  onChange, 
  label = "Brand (Optional)",
  disabled = false,
  error = false,
  helperText = ""
}) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        console.log('Fetching brands...');
        const response = await brandService.getAllBrands({ 
          page: 1, 
          limit: 100, // Get first 100 active brands
          sortBy: 'name',
          sortOrder: 'asc'
        });
        console.log('Brands response:', response);
        const activeBrands = response.brands.filter(brand => brand.isActive);
        console.log('Active brands:', activeBrands);
        setBrands(activeBrands);
        setLoadError(null);
      } catch (err) {
        console.error('Error fetching brands:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          config: err.config
        });
        setLoadError(`Failed to load brands: ${err.message}`);
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const handleChange = (event) => {
    const selectedBrandId = event.target.value;
    if (selectedBrandId === '') {
      onChange(null);
    } else {
      const selectedBrand = brands.find(brand => brand._id === selectedBrandId);
      onChange(selectedBrand);
    }
  };

  const selectedBrand = value && typeof value === 'object' ? value : 
    brands.find(brand => brand._id === value);

  console.log('BrandSelector render - loading:', loading, 'brands count:', brands.length, 'error:', loadError);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" sx={{ minHeight: 56 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Loading brands...
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {loadError}
      </Typography>
    );
  }

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      <InputLabel id="brand-selector-label">{label}</InputLabel>
      <Select
        labelId="brand-selector-label"
        label={label}
        value={selectedBrand?._id || ''}
        onChange={handleChange}
        renderValue={(selected) => {
          if (!selected) return '';
          const brand = brands.find(b => b._id === selected);
          return brand ? (
            <Box display="flex" alignItems="center" gap={1}>
              {brand.logo && (
                <Avatar 
                  src={brand.logo} 
                  sx={{ width: 24, height: 24 }}
                  alt={brand.name}
                />
              )}
              <Typography>{brand.name}</Typography>
            </Box>
          ) : '';
        }}
      >
        <MenuItem value="">
          <em>None (No brand association)</em>
        </MenuItem>
        {brands.map((brand) => (
          <MenuItem key={brand._id} value={brand._id}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              {brand.logo && (
                <Avatar 
                  src={brand.logo} 
                  sx={{ width: 24, height: 24 }}
                  alt={brand.name}
                />
              )}
              <Box flex={1}>
                <Typography variant="body1">{brand.name}</Typography>
                {brand.industry && (
                  <Typography variant="caption" color="text.secondary">
                    {brand.industry}
                  </Typography>
                )}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default BrandSelector; 